import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { postsApi, mediaUrl, type Post } from '@/lib/api'
import { readingTime } from '@/atz/ArticleRichText'
import './BlogList.css'

const formatDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : ''

const Icon = ({ id }: { id: string }) => (
  <svg className="ico">
    <use href={`#${id}`} />
  </svg>
)

export default function BlogList() {
  const [posts, setPosts] = useState<Post[] | null>(null)
  const [featured, setFeatured] = useState<Post | null>(null)

  useEffect(() => {
    postsApi
      .list(100)
      .then((res) => setPosts(res.docs))
      .catch(() => setPosts([]))
    postsApi
      .featured()
      .then(setFeatured)
      .catch(() => setFeatured(null))
  }, [])

  const gridPosts = posts?.filter((p) => p.id !== featured?.id) ?? null
  const featAuthor =
    featured && typeof featured.author === 'object' ? featured.author.name : undefined
  const featTag = featured?.tags && featured.tags.length > 0 ? featured.tags[0].tag : undefined
  const featRt = featured ? readingTime(featured.content) : 0

  return (
    <main id="top">

      <section className="bl-hero">
        <div className="wrap-wide">
          <span className="eyebrow">Knowledge & growth hub</span>
          <h1>Federal contracting, explained clearly.</h1>
        </div>
      </section>

      <section className="sec-pad" style={{ padding: '56px 0 40px' }}>
        <div className="wrap-wide">

          {featured && (
            <Link className="bl-feat" to={`/${featured.slug}/`}>
              <div className="media">
                {featured.coverImage ? (
                  <img
                    src={mediaUrl(featured.coverImage, 'hero')}
                    alt={featured.coverImage.alt || featured.title}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <div className="ph" data-label="ATZ Solution" />
                )}
              </div>
              <div className="body">
                <span className="pill blue">{featTag ? `Featured · ${featTag}` : 'Featured'}</span>
                <h2>{featured.title}</h2>
                {featured.excerpt && <p>{featured.excerpt}</p>}
                <div className="bl-meta">
                  {featured.publishedAt && (
                    <span><Icon id="i-cal" /> {formatDate(featured.publishedAt)}</span>
                  )}
                  {featAuthor && <span><Icon id="i-user" /> {featAuthor}</span>}
                  {featRt > 0 && <span><Icon id="i-clock" /> {featRt} min read</span>}
                </div>
                <span className="lk">Read the full article <span className="arw">→</span></span>
              </div>
            </Link>
          )}

          {gridPosts === null ? (
            <p style={{ color: 'var(--muted)' }}>Loading articles…</p>
          ) : gridPosts.length === 0 ? (
            <p style={{ color: 'var(--muted)' }}>
              {featured ? '' : 'No articles published yet. Check back soon.'}
            </p>
          ) : (
            <div className="bl-grid" style={featured ? { marginTop: '54px' } : undefined}>
              {gridPosts.map((post) => {
                const authorName =
                  typeof post.author === 'object' ? post.author.name : undefined
                return (
                  <Link key={post.id} className="bl-card" to={`/${post.slug}/`}>
                    <div className="media">
                      {post.coverImage ? (
                        <img
                          src={mediaUrl(post.coverImage, 'card')}
                          alt={post.coverImage.alt || post.title}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <div className="ph" data-label="ATZ Solution" />
                      )}
                    </div>
                    <div className="body">
                      <h3>{post.title}</h3>
                      {post.excerpt && <p>{post.excerpt}</p>}
                      <div className="foot">
                        <span>{formatDate(post.publishedAt)}</span>
                        {authorName && <span className="rd">{authorName}</span>}
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}

          <div className="bl-news">
            <div>
              <span className="seal"><span>A</span></span>
              <h2>Reading is a great start. Winning is better.</h2>
              <p>Skip the guesswork. Book a free consultation and a specialist will map the exact registrations and certifications your business needs to compete for federal contracts.</p>
            </div>
            <div className="bl-news-act">
              <Link className="btn btn-light" to="/#contact">Book a Free Consultation <span className="arw">→</span></Link>
              <Link className="btn btn-red" to="/sam-registration/">Start with SAM.gov Registration</Link>
            </div>
          </div>
        </div>
      </section>

    </main>
  )
}
